'use client';

import Image, { type ImageProps } from 'next/image';
import { useEffect, useState } from 'react';

import { DocumentImageSize } from '@/lib/types';
import { DocumentTypeCode } from '@/apiClient';
import { useGetImageByEntityId } from '@/hooks/useGetImageByEntityId';

import { styled, type BoxProps } from '@/primitives';

const EMPTY_SVG_DATA_URI =
  'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>';

const ImageWithSx = styled(Image)({});

function isBlob(value: unknown): value is Blob {
  return value instanceof Blob;
}

export interface DocumentImageProps extends Omit<ImageProps, 'src'> {
  documentTypeCode: DocumentTypeCode;
  documentEntityId: string;
  documentImageSize?: DocumentImageSize;
  fallbackImage?: string;
  sx?: BoxProps['sx'];
}

export function DocumentImage({
  alt,
  documentEntityId,
  documentTypeCode,
  documentImageSize = DocumentImageSize.s,
  fallbackImage,
  ...props
}: DocumentImageProps) {
  const [srcData, _setSrcData] = useState<string>(
    () => fallbackImage || EMPTY_SVG_DATA_URI,
  );
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  const { data: response } = useGetImageByEntityId(
    documentEntityId,
    documentTypeCode,
    documentImageSize,
  );

  useEffect(() => {
    const data = response?.data;
    if (isBlob(data) && data.size > 0) {
      const objectUrl = URL.createObjectURL(data);
      setObjectUrl(objectUrl);
    } else {
      setObjectUrl(null);
    }
  }, [response]);

  if (objectUrl) {
    return <ImageWithSx {...props} src={objectUrl} alt={alt} unoptimized />;
  } else {
    return (
      <ImageWithSx
        {...props}
        alt={alt}
        className="fallback"
        priority
        src={srcData}
        unoptimized
      />
    );
  }
}
